import React from "react";
import styled from "styled-components";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "./button/Button";
import { Flex } from "./compositeComponents/FlexHelpers";
import { Modal } from "./Modal";

const QR_INLINE_SIZE_PX = 72;
const QR_LARGE_SIZE_PX = 320;
// Size written into the downloaded file. The QR code is a vector graphic with a viewBox,
// so a large size keeps it sharp when printed.
const QR_DOWNLOAD_SIZE_PX = 1024;

type DownloadableQrCodeProps = {
    url:string,
    fileName:string,
    disabled?:boolean
};

// Renders a QR code for a link, with a button that saves it as an SVG file.
// Clicking the QR code opens a larger version.
export function DownloadableQrCode(props:DownloadableQrCodeProps):JSX.Element
{
    const [showLarge, setShowLarge] = React.useState(false);
    const containerRef = React.useRef<HTMLButtonElement>(null);

    function handleDownload()
    {
        const svgEl = containerRef.current?.querySelector('svg');
        if(!svgEl)
            return;

        const downloadEl = svgEl.cloneNode(true) as SVGElement;
        downloadEl.setAttribute('width', QR_DOWNLOAD_SIZE_PX.toString());
        downloadEl.setAttribute('height', QR_DOWNLOAD_SIZE_PX.toString());

        const blob = new Blob([new XMLSerializer().serializeToString(downloadEl)], { type:'image/svg+xml' });
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = props.fileName;
        link.click();
        URL.revokeObjectURL(blobUrl);
    }

    return (
        <>
            <Flex flexDirection="row" gap={8} alignItems="center">
                <StyledQrButton type="button"
                    ref={containerRef}
                    title="Vis stor QR kode"
                    onClick={() => setShowLarge(true)}>
                    <QRCodeSVG value={props.url} size={QR_INLINE_SIZE_PX} />
                </StyledQrButton>

                <Button variant="outlined"
                    icon="interface_actions_download"
                    toolTipText="Gem QR kode som fil"
                    disabled={props.disabled}
                    size="32px"
                    onClick={handleDownload} />
            </Flex>

            <Modal title=""
                showModal={showLarge}
                onCancel={() => setShowLarge(false)}
                content={
                    <Flex flexDirection="column" alignItems="center" gap={16}>
                        <QRCodeSVG value={props.url} size={QR_LARGE_SIZE_PX} />
                    </Flex>
                } />
        </>
    );
}

const StyledQrButton = styled.button`
    display: flex;
    padding: 0;
    border: none;
    background: none;
    line-height: 0;
    cursor: pointer;
`;
